import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "../lib/api";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", agency_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await register(form);
      toast.success("Agency created — welcome to Momento!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background relative z-10">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="text-2xl font-display font-extrabold tracking-tight">Momento</span>
        </div>
        <h1 className="text-3xl font-display font-bold tracking-tight mb-1.5">Create your agency</h1>
        <p className="text-muted-foreground mb-8 text-sm">Set up your top-level agency account in seconds.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="agency">Agency name</Label>
            <Input id="agency" value={form.agency_name} onChange={set("agency_name")} placeholder="Acme Growth Co."
              data-testid="register-agency-input" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Your name</Label>
            <Input id="name" value={form.name} onChange={set("name")} placeholder="Jane Doe"
              data-testid="register-name-input" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={set("email")}
              data-testid="register-email-input" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={form.password} onChange={set("password")}
              data-testid="register-password-input" required minLength={6} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full h-11 font-semibold transition-transform hover:scale-[1.01] active:scale-[0.99]"
            disabled={loading} data-testid="register-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create agency"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
